import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import SmartImage from "@/components/SmartImage";
import { Star, Quote, BadgeCheck, ArrowRight } from "lucide-react";

export default function ReviewSpotlight() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/reviews/spotlight")
      .then((r) => setData(r.data?.spotlight || null))
      .catch((err) => console.error("Spotlight load failed:", err))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || !data) return null;
  const { book, review, month_label } = data;
  const cover = book.cover_url || (book.cover_path ? `${API}/files/${book.cover_path}` : null);

  return (
    <section
      data-testid="review-spotlight"
      className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Cover */}
        <div className="lg:col-span-4">
          <Link to={`/book/${book.id}`} data-testid="spotlight-book-link" className="block group">
            <div className="aspect-[3/4] overflow-hidden bg-linen book-card-cover">
              {cover && (
                <SmartImage
                  src={cover}
                  alt={book.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              )}
            </div>
          </Link>
        </div>

        {/* Quote + meta */}
        <div className="lg:col-span-8">
          <div className="label-eyebrow text-sage flex items-center gap-2">
            <Star className="w-3.5 h-3.5 fill-terracotta text-terracotta" strokeWidth={0} />
            Best reviewed · {month_label}
          </div>

          <Quote className="w-8 h-8 md:w-10 md:h-10 text-terracotta mt-6 mb-2" strokeWidth={1} />
          <blockquote
            data-testid="spotlight-quote"
            className="font-serif italic text-2xl sm:text-3xl md:text-4xl leading-tight text-ink"
          >
            "{review.comment}"
          </blockquote>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-4 h-4 ${
                    n <= review.rating ? "fill-terracotta text-terracotta" : "text-edge"
                  }`}
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <div className="text-sm text-muted_ink">
              <span className="font-serif text-ink">{review.user_name}</span>
              {review.verified_purchase && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-sage border border-sage/40 bg-sage/5 px-1.5 py-0.5">
                  <BadgeCheck className="w-3 h-3" strokeWidth={2} /> Verified reader
                </span>
              )}
            </div>
          </div>

          <div className="mt-8 border-t border-edge pt-6 flex items-baseline justify-between flex-wrap gap-3">
            <div>
              <div className="label-eyebrow text-muted_ink">On</div>
              <Link
                to={`/book/${book.id}`}
                className="font-serif text-2xl md:text-3xl hover:text-terracotta transition-colors"
              >
                {book.title}
              </Link>
              <div className="text-sm italic text-muted_ink mt-1">by {book.author}</div>
            </div>
            <Link
              to={`/book/${book.id}`}
              data-testid="spotlight-cta"
              className="btn-primary"
            >
              Read the book <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
